import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import isEqual from 'lodash.isequal';
import groupBy from 'lodash.groupby';

import { CollapseTable } from '@molecules';
import { EditNoteModal } from '@organisms/Modals';
import { useModalState } from '@hooks';

import { TableLinksManagement } from './types';
import { getColumns } from './utils';
import { LinkReviewHistoricalRecordModal } from '@organisms/Modals/LinkReviewHistoricalRecordModal/LinkReviewHistoricalRecordModal';
import { FilterItem, TableSelectType } from '@services';
import QueryKeys from '@constants/query-keys';
import { FilterCloseWrap } from '@molecules/Filters/FilterCloseWrap';
import ColumnFilter from './components/ColumnFilter';
import { ApiClientEnterpriseLinksReview } from '@helpers/apiClients';
import { useEditLinkReview } from './api/edit-links-review';

const FILTERS_LIST: FilterItem[] = [
  {
    name: 'Status',
    key: 'status',
    getFilter: (_onChange, onClose, setFilters, filter): ReactNode => (
      <FilterCloseWrap label="Status" width="initial" onClose={onClose}>
        <ColumnFilter
          key="status"
          paramKey="status"
          onChange={(status: any) => setFilters({ ...filter, status })}
        />
      </FilterCloseWrap>
    ),
  },
  {
    name: 'Category',
    key: 'assetCategory',
    getFilter: (_onChange, onClose, setFilters, filter): ReactNode => (
      <FilterCloseWrap label="Category" width="initial" onClose={onClose}>
        <ColumnFilter
          key="assetCategory"
          paramKey="assetCategory"
          onChange={(assetCategory: any) => setFilters({ ...filter, assetCategory })}
        />
      </FilterCloseWrap>
    ),
  },
];

const EnterpriseLinksReviewTable = () => {
  const { id: organizationId } = useParams<{ id: string }>();

  const [selectedRows, setSelectedRows] = useState<TableLinksManagement[]>([]);
  const [editingNote, setEditingNote] = useState<string>('');

  const [openNoteModal, setOpenNoteModal] = useModalState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState<TableLinksManagement | null>(null);

  const { mutate: editLink, isLoading } = useEditLinkReview({
    mutationConfig: {
      onSuccess: () => {
        setOpenNoteModal(false);
        setEditingNote('');
        setSelectedLink(null);
      },
    },
  });

  const columns = useMemo(
    () =>
      getColumns({
        setSelectedLink,
        setEditingNote,
        setOpenNoteModal,
        setOpenHistoryModal,
      }),
    [setSelectedLink, setEditingNote, setOpenNoteModal, setOpenHistoryModal],
  );

  const getSelectedMultipleValue = useCallback(
    (items: any[], selectedItemsId: string[]) => {
      const allChildren: TableLinksManagement[] = items.reduce(
        (acc, item) => acc.concat(item.child),
        [],
      );
      const selectedItems = allChildren.filter((item) =>
        selectedItemsId.includes((item as { id: string })?.id),
      );

      if (!isEqual(selectedRows, selectedItems)) {
        setSelectedRows(selectedItems);
      }

      const uniqStatuses = selectedItems.reduce(
        (statuses: string[], { status }) =>
          statuses.includes(status) ? statuses : [...statuses, status],
        [],
      );
      const status = uniqStatuses.length > 1 ? 'mixed' : uniqStatuses[0];

      const uniqCategories = selectedItems.reduce(
        (categories: string[], { assetCategory }) =>
          categories.includes(assetCategory) ? categories : [...categories, assetCategory],
        [],
      );
      const category = uniqCategories.length > 1 ? 'mixed' : uniqCategories[0];

      const uniqURLs = selectedItems.reduce(
        (urls: string[], { url }) => (urls.includes(url) ? urls : [...urls, url]),
        [],
      );

      return { status, category, urls: uniqURLs };
    },
    [selectedRows],
  );

  const transformData = (items?: any) => {
    if (!items) {
      return [];
    }

    let result: any = groupBy(items, (item: any) => item.domain);
    result = Object.entries(result).map(([domain, child], index: number) => ({
      label: domain,
      child,
      id: index,
    }));
    return result;
  };

  const handleNoteUpdate = (note: string) => {
    if (!organizationId || !selectedLink) {
      return;
    }

    editLink({
      companyId: organizationId,
      linkId: selectedLink.id,
      data: {
        note,
      },
    });
  };

  const onCloseHistoricalRecord = () => {
    setOpenHistoryModal(false);
    setSelectedLink(null);
  };

  const onCloseEditNoteModal = () => setOpenNoteModal(false);

  const onSubmitNote = (newNote: string) => handleNoteUpdate(newNote);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
      }}
    >
      <CollapseTable<TableLinksManagement>
        queryName={QueryKeys.enterpriseLinksReview}
        columns={columns}
        searchPlaceholder="Search by Title, URL or Domain"
        apiClient={ApiClientEnterpriseLinksReview}
        apiRoute={`/enterprise/${organizationId}/link-reviews`}
        filtersList={FILTERS_LIST}
        rowsPerPageOptions={[10, 20, 50, 100, 200]}
        checkboxSelection
        hideFooterSelectedRowCount={false}
        selectedRowType={TableSelectType.LinksReview}
        getSelectedMultipleValue={getSelectedMultipleValue}
        transformData={transformData}
        pinnedColumns={{ left: ['check', 'actions', 'assetTitle'] }}
        tableSx={{
          width: '100%',
          '& .MuiCollapse-root.MuiCollapse-vertical': {
            width: 'fit-content',
          },
        }}
      />
      <LinkReviewHistoricalRecordModal
        open={openHistoryModal}
        companyId={organizationId}
        id={selectedLink?.id}
        onCancel={onCloseHistoricalRecord}
      />
      <EditNoteModal
        open={openNoteModal}
        isLoading={isLoading}
        onCancel={onCloseEditNoteModal}
        onSubmit={onSubmitNote}
        note={editingNote}
      />
    </Box>
  );
};

export default EnterpriseLinksReviewTable;
